import { isPositiveNumber } from "et3-type-guards";
import {
  CURRENT_MONTH_CALCULATION_ARRAY,
  PERCENTAGE_CONVERSION_FACTOR,
  NEGATIVE_VALUE,
  ZERO_VALUE
} from "../../constants/input-entries-constants";
import {
  TCurrentMonth,
  TDataInputObject,
  TReceiptCompensationTitles,
  TEventCountKeys,
  TIssuesCountKeys,
  TConfigurationReceipt,
  TAuditedEventCountKeys
} from "../../Types";
import { et3ReadableDecimalNumber } from "../et3-data-helper";

export const getCompensationAuditType = (
  eventType: TReceiptCompensationTitles
): {
  issuesCount: TIssuesCountKeys;
  auditCount: TAuditedEventCountKeys;
  eventCount: TEventCountKeys;
} => {
  switch (eventType) {
    case "description":
      return {
        issuesCount: "descriptionIssuesCount",
        auditCount: "descriptionAuditedCount",
        eventCount: "descriptionEventCount"
      };
    case "picture":
      return {
        issuesCount: "pictureIssuesCount",
        auditCount: "pictureAuditedCount",
        eventCount: "pictureEventCount"
      };
    case "report":
      return {
        issuesCount: "reportIssuesCount",
        auditCount: "reportAuditedCount",
        eventCount: "reportEventCount"
      };
    case "investigationCases":
      return {
        issuesCount: "investigationCaseIssuesCount",
        auditCount: "investigationCasesAuditedCount",
        eventCount: "investigationCasesEventCount"
      };
  }
};

export const issuePercentageCalculation = (
  dataInputObject: TDataInputObject,
  eventType: TReceiptCompensationTitles
) => {
  const eventsType = getCompensationAuditType(eventType);
  const auditedCount = dataInputObject[eventsType.auditCount];
  const issuesCount = dataInputObject[eventsType.issuesCount];
  return isPositiveNumber(auditedCount)
    ? (issuesCount / auditedCount) * PERCENTAGE_CONVERSION_FACTOR
    : ZERO_VALUE;
};

export const getIssuesPercentage = (
  eventNumber: number,
  auditedNumber: number,
  issuesCount: number,
  threshold: number,
  pricingPerHundered: number,
  discountPercentage: number
) => {
  if (!isPositiveNumber(auditedNumber)) {
    return ZERO_VALUE;
  }
  const issuePercentage =
    (issuesCount / auditedNumber) * PERCENTAGE_CONVERSION_FACTOR;
  const thresholdPercentage = threshold;
  const pricing =
    (eventNumber * pricingPerHundered) / PERCENTAGE_CONVERSION_FACTOR;
  const discountData =
    (pricing * discountPercentage) / PERCENTAGE_CONVERSION_FACTOR;
  const negativeDiscountData = discountData * NEGATIVE_VALUE;
  return issuePercentage > thresholdPercentage
    ? negativeDiscountData
    : ZERO_VALUE;
};

export const calculateAndUpdateCompensationDiscount = (
  dataInputObject: TDataInputObject,
  eventType: TReceiptCompensationTitles,
  configurationReceipt: TConfigurationReceipt,
  currentMonthReceipt: TCurrentMonth
) => {
  const eventsType = getCompensationAuditType(eventType);
  const events = dataInputObject[eventsType.eventCount];
  const auditedEvents = dataInputObject[eventsType.auditCount];
  const issuesCount = dataInputObject[eventsType.issuesCount];
  const threshold = configurationReceipt[eventType].threshold;
  const pricingPerHundered = configurationReceipt[eventType].price;
  const discountPercentage = configurationReceipt[eventType].discount;
  const pricing = pricingPerHundered / PERCENTAGE_CONVERSION_FACTOR;
  const priceOfEvents = events * pricing;
  const discountData = getIssuesPercentage(
    events,
    auditedEvents,
    issuesCount,
    threshold,
    pricingPerHundered,
    discountPercentage
  );
  return {
    ...currentMonthReceipt,
    [eventType]: {
      price: et3ReadableDecimalNumber(priceOfEvents, 2),
      discount: et3ReadableDecimalNumber(discountData, 2)
    }
  };
};

export const getReceiptFinalSalary = (
  dataInputObject: TDataInputObject,
  configurationReceipt: TConfigurationReceipt
) => {
  return CURRENT_MONTH_CALCULATION_ARRAY.map((eventType) => {
    const eventsType = getCompensationAuditType(eventType);
    const events = dataInputObject[eventsType.eventCount];
    const auditedEvents = dataInputObject[eventsType.auditCount];
    const issuesCount = dataInputObject[eventsType.issuesCount];
    const threshold = configurationReceipt[eventType].threshold;
    const pricingPerHundered = configurationReceipt[eventType].price;
    const discountPercentage = configurationReceipt[eventType].discount;
    const pricing = pricingPerHundered / PERCENTAGE_CONVERSION_FACTOR;
    const priceOfEvents = events * pricing;
    const discountData = getIssuesPercentage(
      events,
      auditedEvents,
      issuesCount,
      threshold,
      pricingPerHundered,
      discountPercentage
    );
    return (
      et3ReadableDecimalNumber(priceOfEvents, 2) +
      et3ReadableDecimalNumber(discountData, 2)
    );
  }).reduce((acc, curr) => acc + curr, 0);
};
